/**
 * CL-PROPERTY-OS-002C3B — Client-side invite lifecycle validation for recovery (fail-closed).
 */

import { isInviteInvalidated } from "./canonicalPortalInviteStatus";
import { normalizeRecoveryEmail } from "./portalInviteRecoveryModel";
import { portalEmailVerificationSatisfiedFromUser } from "./portalEmailVerificationRolloutPolicy";

function safeStr(value) {
  return String(value ?? "").trim();
}

/** Client Firestore Timestamp shape (seconds/nanoseconds + toDate). */
export function isClientFirestoreTimestamp(value) {
  if (!value || typeof value !== "object") return false;
  if (typeof value.toDate !== "function") return false;
  try {
    const date = value.toDate();
    return date instanceof Date && !Number.isNaN(date.getTime());
  } catch {
    return false;
  }
}

export function validateInviteExpiryStateClient(invite = {}) {
  if (!Object.prototype.hasOwnProperty.call(invite, "expiresAt") || invite.expiresAt == null) {
    return { ok: true };
  }
  if (!isClientFirestoreTimestamp(invite.expiresAt)) {
    return { ok: false, reason: "invite_expiry_malformed" };
  }
  if (invite.expiresAt.toDate() < new Date()) {
    return { ok: false, reason: "invite_expired" };
  }
  return { ok: true };
}

export function isInviteLegitimatelyUnusedClient(invite = {}) {
  if (!invite || typeof invite !== "object") return false;
  if (!Object.prototype.hasOwnProperty.call(invite, "used")) return true;
  return invite.used === false;
}

export function hasContradictoryUnusedConsumptionEvidence(invite = {}) {
  if (!isInviteLegitimatelyUnusedClient(invite)) return false;
  if (safeStr(invite.usedBy)) return true;
  if (invite.usedAt != null) return true;
  return false;
}

export function validateInviteRedeemedRetryEvidenceClient(invite = {}, authUid = "") {
  if (invite?.used !== true) {
    return { ok: false, reason: "invite_not_used" };
  }
  const usedBy = safeStr(invite?.usedBy);
  if (!usedBy || usedBy !== safeStr(authUid)) {
    return { ok: false, reason: "used_by_other" };
  }
  if (!isClientFirestoreTimestamp(invite?.usedAt)) {
    return { ok: false, reason: "invite_used_at_missing_or_malformed" };
  }
  return { ok: true };
}

/**
 * Fail-closed invite evidence validation for reload recovery.
 */
export function validatePortalInviteRecoveryEvidence({
  authUser = null,
  inviteData = null,
  inviteCode = "",
} = {}) {
  const uid = safeStr(authUser?.uid);
  const email = normalizeRecoveryEmail(authUser?.email);
  const code = safeStr(inviteCode);

  if (!portalEmailVerificationSatisfiedFromUser(authUser)) {
    return { ok: false, reason: "auth_not_verified" };
  }
  if (!uid || !email) {
    return { ok: false, reason: "auth_missing" };
  }
  if (!code || !inviteData) {
    return { ok: false, reason: "invite_unavailable" };
  }

  const targetEmail = normalizeRecoveryEmail(inviteData.targetEmailLower || inviteData.email);
  if (!targetEmail) {
    return { ok: false, reason: "invite_recipient_missing" };
  }
  if (targetEmail !== email) {
    return { ok: false, reason: "email_mismatch" };
  }

  if (isInviteInvalidated(inviteData)) {
    return { ok: false, reason: "invite_invalidated" };
  }

  const expiry = validateInviteExpiryStateClient(inviteData);
  if (!expiry.ok) {
    return { ok: false, reason: expiry.reason };
  }

  if (isInviteLegitimatelyUnusedClient(inviteData)) {
    if (hasContradictoryUnusedConsumptionEvidence(inviteData)) {
      return { ok: false, reason: "invite_malformed_used_state" };
    }
    return { ok: true, mode: "finish_setup" };
  }

  if (inviteData.used === true) {
    const retry = validateInviteRedeemedRetryEvidenceClient(inviteData, uid);
    if (!retry.ok) {
      return { ok: false, reason: retry.reason };
    }
    return { ok: true, mode: "property_os_retry" };
  }

  return { ok: false, reason: "invite_malformed_used_state" };
}
